import { useState, useEffect, useCallback } from 'react';
import { waitForHydrationComplete } from '@/utils/hydrationManager';

/**
 * GDPR Cookie Consent Hook
 * Verifica lo stato del consenso Iubenda prima di salvare dati non essenziali
 */

// Purpose IDs per Iubenda (TCF v2)
const IUBENDA_PURPOSES = {
  NECESSARY: 1,       // Sempre permesso
  PREFERENCES: 2,     // Preferenze utente (tema, lingua)
  ANALYTICS: 3,       // Analytics (Google Analytics)
  MARKETING: 4,       // Marketing e advertising
  SOCIAL: 5,          // Social media
} as const;

interface CookieConsentState {
  hasConsent: boolean;
  isLoading: boolean;
  preferences: {
    necessary: boolean;
    preferences: boolean;
    analytics: boolean;
    marketing: boolean;
  };
}

interface IubendaCsConfiguration {
  consent?: {
    purposes?: Record<number, boolean>;
  };
}

interface IubendaObject {
  push?: (args: unknown[]) => void;
  csConfiguration?: IubendaCsConfiguration;
}

// Use module augmentation pattern to avoid conflicts
const getIubenda = (): IubendaObject | undefined => {
  if (typeof window !== 'undefined') {
    return (window as unknown as { _iub?: IubendaObject })._iub;
  }
  return undefined;
};

export function useCookieConsent(): CookieConsentState & {
  canStorePreferences: boolean;
  canTrackAnalytics: boolean;
  canTrackMarketing: boolean;
  refreshConsent: () => void;
} {
  const [state, setState] = useState<CookieConsentState>({
    hasConsent: false,
    isLoading: true,
    preferences: {
      necessary: true, // Sempre true
      preferences: false,
      analytics: false,
      marketing: false,
    },
  });

  const checkConsent = useCallback(() => {
    // Controlla se Iubenda è caricato
    if (typeof window === 'undefined') {
      return;
    }

    const iub = getIubenda();
    
    // Se Iubenda non è ancora caricato, riprova dopo
    if (!iub?.csConfiguration?.consent?.purposes) {
      setState(prev => ({ ...prev, isLoading: true }));
      return;
    }

    const purposes = iub.csConfiguration.consent.purposes;

    setState({
      hasConsent: true,
      isLoading: false,
      preferences: {
        necessary: true,
        preferences: purposes[IUBENDA_PURPOSES.PREFERENCES] ?? false,
        analytics: purposes[IUBENDA_PURPOSES.ANALYTICS] ?? false,
        marketing: purposes[IUBENDA_PURPOSES.MARKETING] ?? false,
      },
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    waitForHydrationComplete().then(() => {
      if (cancelled) return;

      // Check iniziale
      checkConsent();

      // Fallback: se dopo 3 secondi Iubenda non è ancora pronto, assume no consent
      timeout = setTimeout(() => {
        setState(prev => {
          if (prev.isLoading) {
            return { ...prev, isLoading: false };
          }
          return prev;
        });
      }, 3000);

      // Listener per quando l'utente dà il consenso
      const iub = getIubenda();
      if (iub?.push) {
        iub.push(['onConsentGiven', checkConsent]);
        iub.push(['onConsentRejected', checkConsent]);
        iub.push(['onPreferenceExpressed', checkConsent]);
      }
    });

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [checkConsent]);

  return {
    ...state,
    canStorePreferences: state.preferences.preferences,
    canTrackAnalytics: state.preferences.analytics,
    canTrackMarketing: state.preferences.marketing,
    refreshConsent: checkConsent,
  };
}

// Esporta costanti per uso esterno
export { IUBENDA_PURPOSES };
